import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth, SESSION_MODES } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ConfirmProvider } from './components/ConfirmDialog';
import './App.css';

import LoginPage           from './pages/auth/LoginPage';
import SignupPage          from './pages/auth/SignupPage';
import ForgotPassword     from './pages/auth/ForgotPassword';
import ResetPassword      from './pages/auth/ResetPassword';
import MasterLayout        from './components/master/MasterLayout';
import MasterDashboard     from './pages/master/MasterDashboard';
import MasterTenants       from './pages/master/MasterTenants';
import MasterTenantForm    from './pages/master/MasterTenantForm';
import MasterTenantView    from './pages/master/MasterTenantView';
import MasterBilling       from './pages/master/MasterBilling';
import MasterSystem        from './pages/master/MasterSystem';
import MasterAdmins        from './pages/master/MasterAdmins';
import MasterLeads         from './pages/master/MasterLeads';
import MasterCampaigns     from './pages/master/MasterCampaigns';
import MasterBlog          from './pages/master/blog/Blog';
import MasterBlogEditor    from './pages/master/blog/BlogEditor';
import TenantLayout        from './components/tenant/TenantLayout';
import TenantDashboard     from './pages/tenant/TenantDashboard';
import TenantAppointments  from './pages/tenant/TenantAppointments';
import TenantUsers         from './pages/tenant/TenantUsers';
import TenantBilling       from './pages/tenant/TenantBilling';
import TenantDeposits      from './pages/tenant/TenantDeposits';
import ChoosePlanPage      from './pages/tenant/ChoosePlanPage';
import TenantNotifications from './pages/tenant/TenantNotifications';
import TenantAudit         from './pages/tenant/TenantAudit';
import TenantIntegrations  from './pages/tenant/TenantIntegrations';
import TenantApiTokens     from './pages/tenant/TenantApiTokens';
import TenantWebhooks      from './pages/tenant/TenantWebhooks';
import TenantSettings      from './pages/tenant/TenantSettings';
import TenantServices      from './pages/tenant/TenantServices';
import TenantBusinessHours from './pages/tenant/TenantBusinessHours';
import TenantRoles         from './pages/tenant/TenantRoles';
import TenantOnboarding    from './pages/tenant/TenantOnboarding';
import PublicBooking       from './pages/public/PublicBooking';
import PublicCancel        from './pages/public/PublicCancel';
import PublicNPS           from './pages/public/PublicNPS';
import PublicReschedule    from './pages/public/PublicReschedule';
import TenantClients       from './pages/tenant/TenantClients';
import TenantWaitlist      from './pages/tenant/TenantWaitlist';
import TenantNPS           from './pages/tenant/TenantNPS';
import TenantIntakeForms   from './pages/tenant/TenantIntakeForms';
import TenantLoyalty       from './pages/tenant/TenantLoyalty';
import TenantPackages      from './pages/tenant/TenantPackages';
import TenantUnits         from './pages/tenant/TenantUnits';

function Spinner() {
  return <div className="loading-screen"><div className="spinner" /></div>;
}

function PrivateRoute({ children, masterOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (masterOnly && user.mode !== SESSION_MODES.MASTER_GLOBAL) return <Navigate to="/app" replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mode === SESSION_MODES.MASTER_GLOBAL) return <Navigate to="/master" replace />;
  return <Navigate to="/app" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login"          element={<LoginPage />} />
      <Route path="/signup"         element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password"  element={<ResetPassword />} />
      <Route path="/"               element={<RootRedirect />} />

      <Route path="/master" element={<PrivateRoute masterOnly><MasterLayout /></PrivateRoute>}>
        <Route index                    element={<MasterDashboard />} />
        <Route path="tenants"           element={<MasterTenants />} />
        <Route path="tenants/new"       element={<MasterTenantForm />} />
        <Route path="tenants/:id"       element={<MasterTenantView />} />
        <Route path="tenants/:id/edit"  element={<MasterTenantForm />} />
        <Route path="billing"           element={<MasterBilling />} />
        <Route path="system"            element={<MasterSystem />} />
        <Route path="admins"            element={<MasterAdmins />} />
        <Route path="leads"             element={<MasterLeads />} />
        <Route path="campaigns"         element={<MasterCampaigns />} />
        <Route path="blog"              element={<MasterBlog />} />
        <Route path="blog/new"          element={<MasterBlogEditor />} />
        <Route path="blog/:id/edit"     element={<MasterBlogEditor />} />
      </Route>

      <Route path="/app" element={<PrivateRoute><TenantLayout /></PrivateRoute>}>
        <Route index                    element={<TenantDashboard />} />
        <Route path="appointments"      element={<TenantAppointments />} />
        <Route path="users"             element={<TenantUsers />} />
        <Route path="billing"           element={<TenantBilling />} />
        <Route path="choose-plan"      element={<ChoosePlanPage />} />
        <Route path="deposits"          element={<TenantDeposits />} />
        <Route path="notifications"     element={<TenantNotifications />} />
        <Route path="audit"             element={<TenantAudit />} />
        <Route path="integrations"      element={<TenantIntegrations />} />
        <Route path="api-tokens"        element={<TenantApiTokens />} />
        <Route path="webhooks"          element={<TenantWebhooks />} />
        <Route path="settings"          element={<TenantSettings />} />
        <Route path="services"          element={<TenantServices />} />
        <Route path="business-hours"    element={<TenantBusinessHours />} />
        <Route path="roles"             element={<TenantRoles />} />
        <Route path="onboarding"        element={<TenantOnboarding />} />
        <Route path="clients"           element={<TenantClients />} />
        <Route path="waitlist"          element={<TenantWaitlist />} />
        <Route path="nps"              element={<TenantNPS />} />
        <Route path="intake-forms"      element={<TenantIntakeForms />} />
        <Route path="loyalty"           element={<TenantLoyalty />} />
        <Route path="packages"          element={<TenantPackages />} />
        <Route path="units"             element={<TenantUnits />} />
      </Route>

      {/* Páginas Públicas — acessíveis sem autenticação */}
      <Route path="/agendar/:slug" element={<PublicBooking />} />
      <Route path="/agendar/:slug/cancelar/:token" element={<PublicCancel />} />
      <Route path="/reagendar/:slug/:token" element={<PublicReschedule />} />
      <Route path="/nps/:token" element={<PublicNPS />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <ConfirmProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: { fontSize: 13, fontFamily: 'Inter, sans-serif' },
            success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
            error:   { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
          }}
        />
      </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}
